import React from 'react';
import { List, Play, X, Settings } from 'lucide-react';
import Button from '../../../components/ui/Button';

const ViewingQueue = ({ queue, onRemoveFromQueue, onPlayNext, isAutoPlay, onAutoPlayChange }) => {
  return (
    <div className="bg-card rounded-lg p-6 shadow-sm border border-border mt-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <List className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-semibold text-foreground text-sm">File d'attente</h3>
        </div>
        
        {queue?.length > 0 && (
          <Button
            size="xs"
            variant="ghost"
            onClick={() => queue?.forEach(item => onRemoveFromQueue?.(item?.id))}
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            Vider
          </Button>
        )}
      </div>

      {/* Auto-play Setting */}
      <div className="flex items-center justify-between p-3 bg-muted/40 rounded-md border border-border mb-4">
        <div className="flex items-center gap-2">
          <Settings className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-foreground">Lecture automatique</span>
        </div>
        
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={isAutoPlay}
            onChange={(e) => onAutoPlayChange?.(e?.target?.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-ring rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-gold"></div>
        </label>
      </div>

      {/* Queue Items */}
      {queue?.length === 0 ? (
        <div className="text-center py-6 text-muted-foreground">
          <List className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
          <p className="text-sm">Aucune vidéo en file d'attente</p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            Utilisez le bouton "+" pour ajouter des vidéos
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {queue?.map((item, index) => (
            <div
              key={item?.id}
              className="flex items-center gap-3 p-3 bg-muted/40 rounded-md border border-border group hover:bg-muted/60 transition-colors"
            >
              {/* Queue Number */}
              <div className="flex-shrink-0 w-6 h-6 bg-brand-gold text-white text-xs rounded-full flex items-center justify-center font-medium">
                {index + 1}
              </div>

              {/* Thumbnail */}
              <div className="flex-shrink-0 w-12 h-8 bg-muted rounded overflow-hidden border border-border">
                <img
                  src={item?.video?.thumbnail || item?.property?.images?.[0] || '/api/placeholder/48/32'}
                  alt={item?.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground line-clamp-1">
                  {item?.property?.title}
                </p>
                <p className="text-xs text-muted-foreground line-clamp-1">
                  {item?.video?.title || 'Visite virtuelle'}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {index === 0 && (
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={onPlayNext}
                    className="w-7 h-7 text-muted-foreground hover:text-foreground hover:bg-accent"
                    title="Lire maintenant"
                  >
                    <Play className="w-4 h-4" />
                  </Button>
                )}
                
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => onRemoveFromQueue?.(item?.id)}
                  className="w-7 h-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                  title="Retirer de la file"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Next Up */}
      {queue?.length > 0 && (
        <div className="mt-4 p-3 bg-muted/40 rounded-md border border-border">
          <div className="flex items-center gap-2 text-foreground">
            <Play className="w-4 h-4 text-brand-gold flex-shrink-0" />
            <span className="text-sm font-medium line-clamp-1">
              Prochaine : {queue?.[0]?.property?.title}
            </span>
          </div>
          
          {isAutoPlay && (
            <p className="text-xs text-muted-foreground mt-1">
              Démarrera automatiquement à la fin de la vidéo actuelle
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ViewingQueue;